/**
 * Property Kit Module Types
 * Types for the inventory/kit management system
 */

// Enums matching database
export type PropertyKitControlType = 'fixed_asset' | 'consumable' | 'countable';
export type PropertyKitAssetStatus = 'present' | 'missing' | 'damaged';
export type PropertyKitConsumableStatus = 'full' | 'half' | 'low' | 'empty';

// Base interfaces
export interface PropertyKitCategory {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PropertyKitItem {
  id: string;
  category_id: string;
  name: string;
  control_type: PropertyKitControlType;
  ideal_quantity: number | null;
  description: string | null;
  image_url: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Joined data
  category?: PropertyKitCategory;
}

export interface PropertyKitTemplate {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropertyKitTemplateItem {
  id: string;
  template_id: string;
  item_id: string;
  default_quantity: number | null;
  created_at: string;
  // Joined data
  item?: PropertyKitItem;
}

export interface PropertyKit {
  id: string;
  property_id: string;
  template_id: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  property?: {
    id: string;
    title: string;
    short_name: string | null;
    images: string[];
  };
  template?: PropertyKitTemplate;
  entries_count?: number;
  missing_count?: number;
}

export interface PropertyKitEntry {
  id: string;
  property_id: string;
  item_id: string;
  current_quantity: number | null;
  ideal_quantity: number | null;
  asset_status: PropertyKitAssetStatus | null;
  consumable_status: PropertyKitConsumableStatus | null;
  created_at: string;
  updated_at: string;
  // Joined data
  item?: PropertyKitItem;
}

export interface PropertyKitHistory {
  id: string;
  entry_id: string;
  changed_by: string;
  previous_value: Record<string, unknown> | null;
  new_value: Record<string, unknown>;
  change_type: 'status_change' | 'quantity_change';
  created_at: string;
  // Joined data
  user?: {
    first_name: string;
    last_name: string;
  };
}

// Form/Input types
export interface CategoryFormData {
  name: string;
  sort_order?: number;
}

export interface ItemFormData {
  category_id: string;
  name: string;
  control_type: PropertyKitControlType;
  ideal_quantity?: number | null;
  description?: string | null;
  image_url?: string | null;
  sort_order?: number;
}

export interface CreateKitInput {
  property_id: string;
  template_id?: string | null;
}

export interface UpdateEntryInput {
  current_quantity?: number | null;
  ideal_quantity?: number | null;
  asset_status?: PropertyKitAssetStatus | null;
  consumable_status?: PropertyKitConsumableStatus | null;
}

// Alert calculations
export interface KitAlert {
  property_id: string;
  property_name: string;
  missing_count: number;
}

// Helper to check if an entry is "missing" based on its type
export function isEntryMissing(entry: PropertyKitEntry, item: PropertyKitItem): boolean {
  switch (item.control_type) {
    case 'fixed_asset':
      return entry.asset_status === 'missing' || entry.asset_status === 'damaged';
    case 'consumable':
      return entry.consumable_status === 'empty' || entry.consumable_status === 'low';
    case 'countable':
      // Use entry's ideal_quantity (property-specific) instead of item's
      if (entry.ideal_quantity == null || entry.current_quantity == null) return false;
      return entry.current_quantity < entry.ideal_quantity;
    default:
      return false;
  }
}

// Status display helpers
export const ASSET_STATUS_LABELS: Record<PropertyKitAssetStatus, string> = {
  present: 'Presente',
  missing: 'Falta',
  damaged: 'Dañado',
};

export const CONSUMABLE_STATUS_LABELS: Record<PropertyKitConsumableStatus, string> = {
  full: 'Lleno',
  half: 'A la mitad',
  low: 'Resurtir',
  empty: 'No hay',
};

export const CONTROL_TYPE_LABELS: Record<PropertyKitControlType, string> = {
  fixed_asset: 'Activo Fijo',
  consumable: 'Consumible',
  countable: 'Contable',
};
